import {Trans} from '@lingui/macro';
import React from 'react';

import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {useRuleEditorContext} from '../../../../editors/ruleEditor/ruleEditorContext';
import {RuleUnrepliedAfterConditionCard} from '../../cards/ruleUnrepliedAfterConditionCard';
import {RuleAddCard} from '../../ruleAddCard';
import {RuleEditorConditions} from '../../ruleEditorConditions';
import {StyledRuleEditorForm} from '../../ruleEditorForm';
import {RuleEditorSection} from '../../ruleEditorSection';
import {RuleEditorRecipeStep} from '../components/ruleEditorRecipeStep';
import {SlaEditorStateActions} from './hooks/useSlaEditorState';
import {SlaEditorActions} from './slaEditorActions';
import {SlaEditorBusinessHours} from './slaEditorBusinessHours';
import {SlaEditorState} from './state/slaEditorState';

/*
 * Props.
 */

interface SlaEditorFormProps {
  state: SlaEditorState;
  actions: SlaEditorStateActions;
  namespace: string;
}

/*
 * Component.
 */

export const SlaEditorForm: React.FC<SlaEditorFormProps> = (props) => {
  const {state, actions, namespace} = props;

  const {ruleIntegration} = useRuleEditorContext();

  const warningUnreplied = state.slaState.unreplied.warning;

  return (
    <StyledRuleEditorForm>
      <RuleEditorRecipeStep step={1}>
        <Trans id="sla-editor-step-1-title">Scope</Trans>
      </RuleEditorRecipeStep>
      <RuleEditorConditions
        description={<Trans id="sla-editor-step-1-conditions-title">Apply this SLA when…</Trans>}
        conditions={state.ruleState.conditions}
        onAddCondition={actions.onAddCondition}
        onUpdateCondition={actions.onUpdateCondition}
        onDeleteCondition={actions.onDeleteCondition}
        onValidation={actions.onValidation}
      />
      <RuleEditorRecipeStep step={2} sideElement={<SlaEditorBusinessHours namespace={namespace} />}>
        <Trans id="sla-editor-step-2-title">Time limits</Trans>
      </RuleEditorRecipeStep>
      <RuleEditorSection
        description={<Trans id="sla-editor-step-2-breach-conditions-title">SLA is breached when…</Trans>}
      >
        <div>
          <RuleUnrepliedAfterConditionCard
            condition={state.slaState.unreplied.breach}
            conditionDefinition={ruleIntegration.findConditionDefinition(state.slaState.unreplied.breach)}
            onUpdate={actions.onUpdateBreachUnreplied}
            isSla
            label={<Trans id="sla-editor-unreplied-label">Conversation is unreplied after</Trans>}
          />
        </div>
      </RuleEditorSection>
      <RuleEditorSection
        description={
          <Trans id="sla-editor-step-2-warning-conditions-title">
            (optional) Add a warning in advance for potential SLA breaches
          </Trans>
        }
      >
        {warningUnreplied ? (
          <div>
            <RuleUnrepliedAfterConditionCard
              condition={warningUnreplied}
              conditionDefinition={ruleIntegration.findConditionDefinition(warningUnreplied)}
              onUpdate={actions.onUpdateWarningUnreplied}
              onDelete={actions.onDeleteWarningUnreplied}
              isSla
              label={<Trans id="sla-editor-unreplied-label">Conversation is unreplied after</Trans>}
            />
          </div>
        ) : (
          <ChromelessButton onClick={actions.onAddWarningUnreplied}>
            <RuleAddCard label="Time limit" />
          </ChromelessButton>
        )}
      </RuleEditorSection>

      <RuleEditorRecipeStep step={3}>
        <Trans id="sla-editor-step-3-title">Actions</Trans>
      </RuleEditorRecipeStep>
      <SlaEditorActions
        description={
          <Trans id="sla-editor-step-3-breach-actions-title">When SLA is breached, do this…</Trans>
        }
        actions={state.slaState.actions.breach}
        tagId={state.slaState.tags.breach}
        onAddAction={actions.onAddBreachAction}
        onUpdateAction={actions.onUpdateBreachAction}
        onDeleteAction={actions.onDeleteBreachAction}
        onValidation={actions.onValidation}
        onSetTag={actions.onSetBreachTag}
      />
      {warningUnreplied ? (
        <SlaEditorActions
          description={
            <Trans id="sla-editor-step-3-warning-actions-title">
              Warn my team about an upcoming SLA breach by doing this…
            </Trans>
          }
          actions={state.slaState.actions.warning}
          tagId={state.slaState.tags.warning}
          onAddAction={actions.onAddWarningAction}
          onUpdateAction={actions.onUpdateWarningAction}
          onDeleteAction={actions.onDeleteWarningAction}
          onValidation={actions.onValidation}
          onSetTag={actions.onSetWarningTag}
        />
      ) : null}
    </StyledRuleEditorForm>
  );
};
